import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MailWarning, UserSearch } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatDateTime } from '../../lib/dateFmt';
import { api, ApiError } from '../../lib/api';

interface FailedLoginAttempt {
  id: string;
  email: string;
  attempts: number;
  first_seen: string;
  last_seen: string;
  last_ip: string | null;
  suggestion: {
    person_id: string;
    full_name: string;
    matched_email: string;
    field: 'work' | 'personal';
  } | null;
}

const KEY = ['admin', 'failed-logins'] as const;

/**
 * Sign-in code requests for addresses that match nobody. The login
 * page deliberately tells the user nothing (no enumeration), so this
 * is where HR finds out someone is consistently typing the wrong
 * email and can ring them. Rows auto-purge after 90 days.
 */
export function FailedLogins() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: () =>
      api<{ attempts: FailedLoginAttempt[] }>('/api/admin/failed-logins'),
  });
  const clear = useMutation({
    mutationFn: (id: string) =>
      api<{ removed: boolean }>(`/api/admin/failed-logins/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Failed to clear'),
  });

  const attempts = data?.attempts ?? [];

  return (
    <main className="mx-auto max-w-5xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={MailWarning}
        eyebrow="System"
        title="Failed sign-ins"
        description="Email addresses people typed at the sign-in page that match nobody on the system. The page they see gives nothing away; this is where you spot someone consistently using the wrong address so you can contact them. Entries disappear after 90 days."
      />

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-5">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
          </div>
        </Card>
      ) : attempts.length === 0 ? (
        <EmptyState
          icon={<MailWarning className="h-6 w-6" />}
          title="No failed sign-ins"
          description="Nobody has requested a code with an unknown email address in the last 90 days."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-divider/10">
            {attempts.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-[16rem] flex-1">
                  <div className="font-medium text-fg">{a.email}</div>
                  <div className="text-xs text-fg-muted">
                    First {formatDateTime(a.first_seen)} · last {formatDateTime(a.last_seen)}
                    {a.last_ip ? ` · ${a.last_ip}` : ''}
                  </div>
                </div>
                <div className="w-28 flex-none">
                  <Pill tone={a.attempts >= 3 ? 'warning' : 'neutral'} size="sm">
                    {a.attempts} attempt{a.attempts === 1 ? '' : 's'}
                  </Pill>
                </div>
                <div className="min-w-[14rem] flex-1 text-sm">
                  {a.suggestion ? (
                    <Link
                      to={`/admin/people/${a.suggestion.person_id}`}
                      className="inline-flex items-center gap-1.5 text-accent hover:underline"
                    >
                      <UserSearch className="h-4 w-4" />
                      Did they mean {a.suggestion.full_name}? ({a.suggestion.matched_email},{' '}
                      {a.suggestion.field})
                    </Link>
                  ) : (
                    <span className="text-xs text-fg-dim">No close match on the roster</span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clear.mutate(a.id)}
                  loading={clear.isPending}
                >
                  Clear
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </main>
  );
}
